import { useEffect } from 'react'
import { Outlet } from 'react-router'
import { usePostHog } from '@posthog/react'
import type { PbBranding } from '@itsrever/rever-api'
import { useSlug } from '@/hooks'
import ScrollToTop from '@/ui/components/scroll-to-top'
import { Error, NotFound } from '@/pages'
import { useOrderShortcut } from '@/hooks/use-order-shortcut'
import { useCustomCopies } from '@/hooks/use-custom-copies'
import { useBugReport } from '@/hooks/use-bug-report'
import { useSettings } from '@/hooks/use-settings'
import { DemoSettings } from '@/app/demo/demo-settings'
import { LoadingOverlay } from '../components/loading-overlay'

export function AppLayout() {
  useBugReport()
  useOrderShortcut()

  const posthog = usePostHog()

  const slug = useSlug()

  const { data: settings, status: settingsStatus } = useSettings({ slug })
  const { status: copiesStatus } = useCustomCopies({ slug })

  const updateTheme = (theme: Partial<PbBranding>) => {
    if (theme?.title) {
      document.title = theme?.title
    }

    if (theme?.favicon_url) {
      const oldFavicon = document.getElementById('dynamic-favicon')
      if (oldFavicon) document.head.removeChild(oldFavicon)

      const favicon = document.createElement('link')
      favicon.id = 'dynamic-favicon'
      favicon.rel = 'shortcut icon'
      favicon.href = theme?.favicon_url
      document.head.appendChild(favicon)
    }
  }

  useEffect(() => {
    if (!settings) return

    const { ecommerce_group, ecommerce_id, is_testing, use_case } = settings

    if (!ecommerce_id) return

    posthog.group('ecommerce', ecommerce_id, {
      group: ecommerce_group,
      use_case: use_case
    })

    posthog.register({
      is_testing_ecommerce: is_testing
    })
  }, [posthog, settings])

  if (settingsStatus === 'pending' || copiesStatus === 'pending')
    return <LoadingOverlay loading={true} />

  if (settingsStatus === 'error' || !settings.is_enabled) return <NotFound />

  if (copiesStatus === 'error') return <Error />

  return (
    <ScrollToTop>
      <>
        <DemoSettings onThemeUpdate={updateTheme} />
        <Outlet />
      </>
    </ScrollToTop>
  )
}
